import { getApiErrorMessage } from "./getApiErrorMessage";
import { translateRbacApiMessage } from "./translateRbacApiMessage";

/** Backend `message` strings for inventory / branch / scope APIs → i18n keys. */
const INVENTORY_API_MESSAGE_TO_KEY = {
  "Forbidden: insufficient permissions": "rbac.apiMsgs.forbiddenInsufficientPermissions",
  "You do not have permission to perform this action. This permission is not available on your account—ask your administrator for access.":
    "rbac.apiMsgs.permissionNotAvailableGeneric",
  "You do not have permission to enable or disable inventory. Ask your administrator to enable Inventory — Enable/disable inventory on your role.":
    "rbac.apiMsgs.permissionDeniedEnableDisableInventory",
  "You do not have permission to enable or disable auto accept inventory. Ask your administrator to enable Inventory — Enable/disable Auto Accept inventory on your role.":
    "rbac.apiMsgs.permissionDeniedEnableDisableInventory",
  "Forbidden": "inventoryPage.apiMsgs.forbidden",
  "Unauthorized": "inventoryPage.apiMsgs.unauthorized",
  "Access denied: company, admin, or staff only": "inventoryPage.apiMsgs.accessDeniedUserType",
  "Access denied. You do not have permission to modify this store.":
    "inventoryPage.apiMsgs.accessDeniedModifyStore",
  "Access denied. You do not have permission to modify this branch.":
    "inventoryPage.apiMsgs.accessDeniedModifyBranch",
  "Access denied. You do not have permission to modify inventory for this branch.":
    "inventoryPage.apiMsgs.accessDeniedModifyBranch",
  "Access denied. Inventory has no branch context.":
    "inventoryPage.apiMsgs.accessDeniedModifyBranch",
  "Unauthorized access.":
    "inventoryPage.apiMsgs.accessDeniedModifyBranch",
  "Forbidden: store or branch is outside your accessible scope":
    "inventoryPage.apiMsgs.scopeOutsideAccessible",
  "Access denied. Store is outside your accessible scope.":
    "inventoryPage.apiMsgs.storeOutsideScope",
  "Access denied. Branch is outside your accessible scope.":
    "inventoryPage.apiMsgs.branchOutsideScope",
  "Access denied. You are not authorized to view any stores.":
    "inventoryPage.apiMsgs.accessDeniedNoStores",
  "Inventory management is not available for Foodics branches.":
    "inventoryPage.apiMsgs.foodicsInventoryNotAvailable",
  "branchId is required": "inventoryPage.apiMsgs.branchIdRequired",
  "inventory_enabled must be a boolean value": "inventoryPage.apiMsgs.inventoryEnabledMustBeBoolean",
  "Invalid branchId format": "inventoryPage.apiMsgs.invalidBranchId",
  "Branch not found": "inventoryPage.apiMsgs.branchNotFound",
  "Branch not found in store": "inventoryPage.apiMsgs.branchNotFoundInStore",
  "Server error while updating inventory enabled status":
    "inventoryPage.apiMsgs.serverErrorUpdateInventoryEnabled",
  "Access denied.": "inventoryPage.apiMsgs.accessDenied",
};

/**
 * @param {unknown} message
 * @param {import('i18next').TFunction} t
 * @returns {string} Translated text, or empty if unknown (caller may use RBAC map or fallback).
 */
export function translateInventoryApiMessage(message, t) {
  if (message == null || message === "") return "";
  const s = String(message).trim();
  const key = INVENTORY_API_MESSAGE_TO_KEY[s];
  if (key) return t(key);
  return "";
}

/**
 * Resolve a user-facing error for inventory page actions (API + fallbacks, EN/AR).
 * @param {unknown} err
 * @param {import('i18next').TFunction} t
 * @param {string} fallbackKey - i18n key when no API message is available
 * @param {string} [productName] - optional interpolation for inline row errors
 * @returns {string}
 */
export function resolveInventoryPageError(err, t, fallbackKey, productName) {
  const raw = getApiErrorMessage(err);
  const fromInventory = raw ? translateInventoryApiMessage(raw, t) : "";
  if (fromInventory) return fromInventory;
  const fromRbac = raw ? translateRbacApiMessage(raw, t) : "";
  if (fromRbac && fromRbac !== raw) return fromRbac;
  if (raw) return raw;
  const opts =
    productName != null && String(productName).trim() !== ""
      ? { product: productName }
      : undefined;
  return t(fallbackKey, opts);
}

export { getApiErrorMessage };
